import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { createElectricityT } from '../../../utils/electricityTranslations'
import { ElectricityContent } from '../ElectricityContent'
import type { ElectricityAnalysis, ElectricityCategory } from '@popety-io/types'
import { OverviewTab } from './tabs/OverviewTab'
import { TrendsTab } from './tabs/TrendsTab'

export interface ElectricitySectionProps {
  entity: {
    id: string
    municipality_id?: string
    geo_center?: {
      lat: number
      lon: number
    }
    npa?: string
    municipality_name?: string
    canton_name?: string
  }
  userId: string
  onDataFetched: (data: ElectricityAnalysis) => void
  getElectricityData: () => ElectricityAnalysis | null
  updateElectricityData: (
    entity: any,
    userId: string,
    category: ElectricityCategory
  ) => Promise<ElectricityAnalysis>
  /** When true, hides filter controls and shows only the current filter value */
  readOnly?: boolean
}

export const ElectricitySection: React.FC<ElectricitySectionProps> = ({
  entity,
  userId,
  onDataFetched,
  getElectricityData,
  updateElectricityData,
  readOnly = false,
}) => {
  const { t: rawT, i18n } = useTranslation()
  const t = createElectricityT(rawT, i18n)

  // State
  const [activeTab, setActiveTab] = useState('overview')
  const [selectedCategory, setSelectedCategory] =
    useState<ElectricityCategory>('H4')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Track fetched params to prevent duplicate requests
  const paramsHashRef = useRef<string>('')

  const fetchElectricityData = useCallback(async () => {
    if (!entity?.id || !userId) {
      return
    }

    if (!entity.municipality_id && !entity.geo_center) {
      return
    }

    const paramsHash = JSON.stringify({
      entityId: entity.id,
      municipalityId: entity.municipality_id,
      lat: entity.geo_center?.lat,
      lon: entity.geo_center?.lon,
      category: selectedCategory,
    })

    // Skip if we already fetched with these exact params
    if (paramsHashRef.current === paramsHash) {
      return
    }

    setLoading(true)
    setError(null)
    paramsHashRef.current = paramsHash

    try {
      const analysis = await updateElectricityData(
        entity,
        userId,
        selectedCategory
      )

      onDataFetched(analysis)
    } catch (err) {
      console.error('Error fetching electricity data:', err)
      const message = err instanceof Error ? err.message : ''
      const isNotFound =
        message.includes('No electricity data found') || message.includes('404')
      setError(
        isNotFound ? t('states.categoryNotAvailable') : t('states.fetchError')
      )
    } finally {
      setLoading(false)
    }
  }, [
    entity,
    userId,
    selectedCategory,
    updateElectricityData,
    onDataFetched,
    t,
  ])

  // Fetch when component mounts or entity/category changes
  useEffect(() => {
    fetchElectricityData()
  }, [fetchElectricityData])

  const data = getElectricityData()

  const handleCategoryChange = useCallback((category: ElectricityCategory) => {
    setSelectedCategory(category)
    // Reset params hash to force re-fetch
    paramsHashRef.current = ''
  }, [])

  const renderTabContent = useCallback(() => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab data={data} t={t} />
      case 'trends':
        return <TrendsTab data={data} t={t} />
      default:
        return <OverviewTab data={data} t={t} />
    }
  }, [activeTab, data, t])

  return (
    <ElectricityContent
      data={data}
      loading={loading}
      error={error}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      selectedCategory={selectedCategory}
      onCategoryChange={handleCategoryChange}
      renderTabContent={renderTabContent}
      locationName={entity?.npa ? `${entity.npa}` : entity?.municipality_name}
      cantonName={entity?.canton_name}
      readOnly={readOnly}
    />
  )
}
